/**
 * Image Analysis API Functions (Gemini Vision)
 */

import { apiClient } from '../api-client';

export interface AnalysisResult {
  id: string;
  workspace_id: string;
  user_id: string;
  image_s3_key: string;
  image_hash: string;
  gemini_model: string;
  system_prompt_used: string;
  user_prompt_used: string;
  system_prompt_generated: string;
  user_prompt_generated: string;
  raw_response: string;
  status: string;
  credits_used: number;
  processing_time_ms: number;
  created_at: string;
  updated_at: string;
  completed_at: string;
}

export interface AnalysisHistoryResponse {
  results: AnalysisResult[];
  total: number;
}

/**
 * @deprecated Use AI Tools API instead (2026-01-19)
 * @see /api/v1/admin2/ai-tools for new tool configuration
 */
export interface GeminiVisionConfig {
  id: string;
  model: string;
  temperature: number;
  top_p: number;
  top_k: number;
  max_output_tokens: number;
  cost_per_analysis: number;
  system_prompt_template: string;
  user_prompt_template: string;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

/**
 * Analyze image with Gemini Vision API
 */
export async function analyzeImage(
  file: File,
  options?: {
    system_prompt?: string;
    user_prompt?: string;
    model_id?: string;
  }
): Promise<AnalysisResult> {
  const formData = new FormData();
  formData.append('file', file);

  if (options?.system_prompt) {
    formData.append('system_prompt', options.system_prompt);
  }
  if (options?.user_prompt) {
    formData.append('user_prompt', options.user_prompt);
  }
  if (options?.model_id) {
    formData.append('model_id', options.model_id);
  }

  const response = await apiClient.post('/api/v1/image-analysis/analyze', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Get analysis history for workspace
 */
export async function getAnalysisHistory(limit: number = 10): Promise<AnalysisResult[]> {
  const response = await apiClient.get('/api/v1/image-analysis/history', {
    params: { limit },
  });
  return response.data;
}

/**
 * @deprecated Use AI Tools API instead (2026-01-19)
 * @see /api/v1/admin2/ai-tools/:toolId for new tool configuration
 * Get Gemini Vision config (admin)
 */
export async function getGeminiVisionConfig(): Promise<GeminiVisionConfig> {
  const response = await apiClient.get('/api/v1/admin/gemini-vision/config');
  return response.data;
}

/**
 * @deprecated Use AI Tools API instead (2026-01-19)
 * @see PUT /api/v1/admin2/ai-tools/:toolId for new tool configuration
 * Update Gemini Vision config (admin)
 */
export async function updateGeminiVisionConfig(
  config: Partial<Omit<GeminiVisionConfig, 'id' | 'created_at' | 'updated_at' | 'updated_by'>>
): Promise<GeminiVisionConfig> {
  const response = await apiClient.put('/api/v1/admin/gemini-vision/config', config);
  return response.data;
}
